import { Component, ElementRef, OnDestroy, computed, effect, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFloatButtonModule } from 'ng-zorro-antd/float-button';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { rxResource } from '@angular/core/rxjs-interop';
import { CachedProjectService } from '../../../shared/services/cached-project.service';
import { CachedUserService } from '../../../shared/services/cached-user.service';
import { take } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { EditDefinitionDialogComponent } from '../../project/dialogs/edit-definition-dialog/edit-definition.component';
import { AddDefinitionDialogComponent } from '../../project/dialogs/add-definition-dialog/add-definition.component';

@Component({
  selector: 'app-parameter-glossary',
  imports: [FormsModule, NzFormModule, NzInputModule, NzIconModule, NzAutocompleteModule, NzDividerModule, NzSpinModule, NzDrawerModule, NzFloatButtonModule, NzMenuModule, NzTagModule, NzButtonModule],
  templateUrl: './parameter-glossary.component.html',
  styleUrl: './parameter-glossary.component.less',
})
export class ParameterGlossaryComponent implements OnDestroy {
  // Input box focus
  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  protected selectedParameter = computed(() => this.cachedProjectService.selectedGlossaryParameter().parameter);
  protected typedParameter = signal('')

  private userDetails = rxResource({
    stream: () => this.cachedUserService.getUserDetails(),
  });

  protected isComputeAdmin = computed(() => !!this.userDetails.value()?.isComputeAdmin)

  protected displayedResult = signal<{ def?: any; error: boolean }>({ def: undefined, error: false });
  protected categories = computed(() => [...new Set(this.paramList.value().map((param: any) => param.category))].sort((a, b) => a.localeCompare(b)));

  // Search by sub string
  private searchTerms(searchText: string): string[] {
    return searchText.toLowerCase().split(/\s+/).filter((term) => term.length > 0);
  }

  // True if any whitespace-separated term in the search text is a substring of value,
  // e.g. searching "awi job" matches names containing "awi" and/or "job".
  private matchesAnyTerm(value: string | undefined, searchText: string): boolean {
    const name = value?.toLowerCase() ?? '';
    const terms = this.searchTerms(searchText);
    return terms.length === 0 || terms.some((term) => name.includes(term));
  }

  // Ranks how well `value` matches the search terms: 0 = exact match to a term, 1 = starts with a
  // term, 2 = a whole word in the (space-delimited) name matches a term, 3 = term appears anywhere
  // as a substring. When multiple terms are typed, the best (lowest) rank across all of them wins,
  // and matching more terms breaks ties in favor of the more-relevant result.
  private rankAgainstTerms(value: string | undefined, terms: string[]): [number, number] {
    const name = value?.toLowerCase() ?? '';
    const words = name.split(' ');
    let bestRank = Infinity;
    let matchCount = 0;
    for (const term of terms) {
      const rank = name === term ? 0
        : name.startsWith(term) ? 1
        : words.includes(term) ? 2
        : name.includes(term) ? 3
        : undefined;
      if (rank !== undefined) {
        matchCount++;
        bestRank = Math.min(bestRank, rank);
      }
    }
    return [bestRank, -matchCount];
  }

  // Sorts by match quality against the search terms first, falling back to alphabetical order
  // (ties, and the no-search-term case where every rank is equal).
  private compareByRank(a: string, b: string, terms: string[]): number {
    const [rankA, negCountA] = this.rankAgainstTerms(a, terms);
    const [rankB, negCountB] = this.rankAgainstTerms(b, terms);
    return rankA - rankB || negCountA - negCountB || a.localeCompare(b);
  }

  // Categories whose name matches the typed text, ranked by match quality; the browse grid and
  // chip row show these. With no search text, every category ties and the order stays alphabetical.
  protected filteredCategories = computed(() => {
    const searchText = this.typedParameter();
    const terms = this.searchTerms(searchText);
    return this.categories()
      .filter((category: any) => this.matchesAnyTerm(category, searchText))
      .sort((a, b) => this.compareByRank(a, b, terms));
  });

  // Tracking for arrow key selection
  protected activeIndex = computed(() => this.searchResults().indexOf(this.selectedParameter()));

  protected glossaryOpen = signal(false);
  // Set right before an internally-driven selection
  private isInternalSelection = false;
  protected bodyStyle = {
    padding: '2%', margin: 0
  }

  constructor(
    private cachedProjectService: CachedProjectService,
    private cachedUserService: CachedUserService,
    private modalService: NzModalService,
  ) {
    effect(() => {
      if (this.parameterDefinition.isLoading()) {
        return;
      }
      if (this.parameterDefinition.error()) {
        this.displayedResult.set({ def: undefined, error: true });
      } else {
        this.displayedResult.set({ def: this.parameterDefinition.value(), error: false });

      }
    });

    effect(() => {
      const parameter = this.cachedProjectService.selectedGlossaryParameter().parameter;
      if (!parameter) {
        return;
      }
      this.glossaryOpen.set(true);
      if (!this.isInternalSelection) {
        this.typedParameter.set(parameter);
      }
      this.isInternalSelection = false;
    });

    effect(() => {
      this.searchInput()?.nativeElement.focus();
    });
  }

  ngOnDestroy() {
    this.cachedProjectService.setSelectedGlossaryParameter('');
  }

  protected openDrawer() {
    this.glossaryOpen.set(true)
    this.setTypedParameter(this.typedParameter())
  }

  protected closeDrawer() {
    this.glossaryOpen.set(false);
    // Clear the shared selection so the next open starts blank
    this.cachedProjectService.setSelectedGlossaryParameter('');
  }

  protected paramList = rxResource({
    defaultValue: [] as any[],
    stream: () => this.cachedProjectService.getParameterList(),
  });

  protected setSelectedParameter(value: string) {
    this.isInternalSelection = true;
    this.cachedProjectService.setSelectedGlossaryParameter(value);
    this.searchInput()?.nativeElement.focus();
  }

  // Updates autocomplete
  protected setTypedParameter(value: string) {
    this.typedParameter.set(value)
    if (this.typedParameter() != "") {
      const firstResult = this.searchResults();
      if (firstResult) {
        this.setSelectedParameter(firstResult[0]);
      }
    } else {
      this.setSelectedParameter("");
    }
  }

  // Moves the selection up/down through searchResults()
  protected onSearchKeydown(event: KeyboardEvent) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    const results = this.searchResults();
    if (!results.length) {
      return;
    }
    event.preventDefault();
    const current = this.activeIndex();
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = current < 0
      ? 0
      : Math.min(Math.max(current + delta, 0), results.length - 1);
    this.setSelectedParameter(results[nextIndex]);
  }

  // Filters search results, ranked by match quality (exact > starts-with > contains). If the term
  // matches exactly to a category, all parameters under that category are shown instead (alphabetical).
  protected searchResults = computed(() => {
    const searchText = this.typedParameter();
    const term = searchText.toLowerCase();
    const params = this.paramList.value();
    if (!term) {
      return params.map((param: any) => param.name).sort((a: string, b: string) => a.localeCompare(b));
    }
    const isCategory = this.categories().some((category: any) => category?.toLowerCase() === term);
    if (isCategory) {
      return params
        .filter((param: any) => param.category?.toLowerCase() === term)
        .map((param: any) => param.name)
        .sort((a: string, b: string) => a.localeCompare(b));
    }
    const terms = this.searchTerms(searchText);
    return params
      .filter((param: any) => this.matchesAnyTerm(param.name, searchText))
      .map((param: any) => param.name)
      .sort((a: string, b: string) => this.compareByRank(a, b, terms));
  });

  // Fills the search bar with a category name to list its parameters.
  protected filterByCategory(category: string) {
    this.setTypedParameter(category);
  }

  protected editDefinition() {
    const name = this.selectedParameter();
    const categories = this.categories();
    if (!name) {
      return;
    }
    this.modalService
      .create({
        nzContent: EditDefinitionDialogComponent,
        nzFooter: null,
        nzData: { name, categories },
      })
      .afterClose.pipe(take(1))
      .subscribe((edited) => {
        if (edited) {
          this.parameterDefinition.reload();
          this.paramList.reload();
        }
      });
  }

  protected addDefinition() {
    const name = this.typedParameter();
    const categories = this.categories();
    if (!name) {
      return;
    }
    this.modalService
      .create({
        nzContent: AddDefinitionDialogComponent,
        nzFooter: null,
        nzData: { name, categories },
      })
      .afterClose.pipe(take(1))
      .subscribe((added) => {
        if (added) {
          this.parameterDefinition.reload();
          this.paramList.reload();
          this.setSelectedParameter(name)
        }
      });
  }

  protected hasValue(value: any): boolean {
    return value != null && value !== '' && value !== '-' && value !== '—';
  }

  protected hasAnyValue(def: any): boolean {
    return [def?.arg_type, def?.default, def?.explanation, def?.category, def?.unit]
      .some((value) => this.hasValue(value));
  }

  protected parameterDefinition = rxResource({
    params: () => {
      const term = this.selectedParameter();
      return term ? { searchName: term } : this.typedParameter() != '' ? null : undefined;
    },
    stream: ({ params }) => {
      return this.cachedProjectService.getParameterDefinition(params!.searchName);
    }
  });
}
